import { GYM_AI_CAP_KEYS, type AdminGymAiLimits, type GymAiCapKey } from '@steado/admin-contract/gyms';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth/auth-context';

import { gymDetailQueryOptions } from './gym-detail-query';
import {
  CAPS,
  capIssue,
  changesOf,
  describeOverride,
  draftOf,
  formatCount,
  hasIssues,
  toUpdateRequest,
  warningsFor,
  type CapDraft,
  type LimitsDraft,
} from './gym-ai-limits';
import { gymAiLimitsQueryOptions, updateGymAiLimits } from './gym-ai-limits-query';

interface AiLimitsDialogProps {
  readonly gymId: string;
  readonly limits: AdminGymAiLimits;
  readonly onClose: () => void;
}

/**
 * Edits the three caps in two steps: the numbers, then a plain restatement of
 * what changes before anything is sent. One dialog for both, so Back keeps
 * what was typed.
 */
export function AiLimitsDialog({ gymId, limits, onClose }: AiLimitsDialogProps) {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<LimitsDraft>(() => draftOf(limits));
  const [step, setStep] = useState<'edit' | 'review'>('edit');

  const changes = changesOf(limits, draft);
  const invalid = hasIssues(draft);
  const warnings = warningsFor(limits, changes);

  const save = useMutation({
    mutationFn: () => updateGymAiLimits(api, gymId, toUpdateRequest(changes)),
    onSuccess: (next) => {
      // The write answers with the read's shape, so the panel updates before
      // the refetches below land.
      queryClient.setQueryData(gymAiLimitsQueryOptions(api, gymId).queryKey, next);
      void queryClient.invalidateQueries({ queryKey: gymAiLimitsQueryOptions(api, gymId).queryKey });
      // The gym page lists the change under Recent activity; the gym list
      // shows the near-limit tag.
      void queryClient.invalidateQueries({ queryKey: gymDetailQueryOptions(api, gymId).queryKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'gyms'] });
      onClose();
    },
    retry: false,
  });

  const setField = (key: GymAiCapKey, next: CapDraft): void => {
    setDraft((current) => ({ ...current, [key]: next }));
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <DialogContent
        aria-label={step === 'edit' ? 'Edit AI limits' : 'Review AI limits'}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>{step === 'edit' ? 'Edit AI limits' : 'Save these limits?'}</DialogTitle>
          <DialogDescription>
            {step === 'edit'
              ? 'Type a number, like 25,000,000 or 25M, or use the default.'
              : 'This takes effect at once and is recorded against your account.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'edit' ? (
          <div className="flex flex-col gap-5">
            {GYM_AI_CAP_KEYS.map((key) => (
              <CapField
                key={key}
                capKey={key}
                defaultValue={limits[key].default}
                draft={draft[key]}
                onChange={(next) => {
                  setField(key, next);
                }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <ul className="flex flex-col gap-3">
              {changes.map((change) => (
                <li key={change.key} className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-muted-foreground">{CAPS[change.key].label}</span>
                  <span className="font-mono text-[13px] text-foreground">
                    {describeOverride(change.from, limits[change.key].default)}
                    {' → '}
                    {describeOverride(change.to, limits[change.key].default)}
                  </span>
                </li>
              ))}
            </ul>
            {warnings.map((warning) => (
              <Alert key={warning} tone="warn" role="note">
                <AlertDescription>{warning}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {save.isError ? (
          <p role="alert" className="text-[13px] leading-relaxed text-err-fg">
            {save.error.message}
          </p>
        ) : null}

        <DialogFooter>
          {step === 'edit' ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                disabled={invalid || changes.length === 0}
                title={!invalid && changes.length === 0 ? 'Nothing has changed.' : undefined}
                onClick={() => {
                  setStep('review');
                }}
              >
                Review changes
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                disabled={save.isPending}
                onClick={() => {
                  save.reset();
                  setStep('edit');
                }}
              >
                Back
              </Button>
              <Button
                disabled={save.isPending || changes.length === 0}
                onClick={() => {
                  save.mutate();
                }}
              >
                {save.isPending ? 'Saving…' : 'Save limits'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CapFieldProps {
  readonly capKey: GymAiCapKey;
  readonly defaultValue: number;
  readonly draft: CapDraft;
  readonly onChange: (next: CapDraft) => void;
}

/** One cap: a number field and a "Use default" switch that shows the default. */
function CapField({ capKey, defaultValue, draft, onChange }: CapFieldProps) {
  const inputId = useId();
  const switchId = useId();
  const issueId = useId();
  const meta = CAPS[capKey];
  const issue = capIssue(capKey, draft);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
        {meta.label}
      </Label>
      <Input
        id={inputId}
        inputMode={meta.unit === 'tokens' ? 'text' : 'numeric'}
        autoComplete="off"
        className="font-mono"
        // With the default on, the field shows it: what applies, not a blank.
        value={draft.useDefault ? formatCount(defaultValue) : draft.text}
        disabled={draft.useDefault}
        aria-invalid={issue !== null}
        {...(issue === null ? {} : { 'aria-describedby': issueId })}
        onChange={(event) => {
          onChange({ ...draft, text: event.target.value });
        }}
      />
      <div className="flex items-center gap-2">
        <Switch
          id={switchId}
          checked={draft.useDefault}
          onCheckedChange={(useDefault) => {
            onChange({ ...draft, useDefault });
          }}
        />
        <Label htmlFor={switchId} className="text-xs font-normal text-muted-foreground">
          Use default ({formatCount(defaultValue)})
        </Label>
      </div>
      {issue === null ? (
        <span className="text-xs text-muted-foreground">{meta.hint}</span>
      ) : (
        <span id={issueId} className="text-xs text-err-fg">
          {issue}
        </span>
      )}
    </div>
  );
}
